import { css } from '@emotion/react';
import React, { ReactNode } from 'react';
import {
  black,
  gray6,
  info,
  white,
} from 'styles/global_defaults/colors';
import {
  headerRegularLineHeight,
  normalFontWeight,
  semiBoldFontWeight,
  textExtraLargeFontSize,
  textLargeLineHeight,
  textMediumFontSize,
} from 'styles/global_defaults/fonts';
import {
  handheld,
  screenMdMax,
} from 'styles/global_defaults/media-queries';
import {
  doubleSpacing,
  extraLargeSpacing,
  halfSpacing,
  largeSpacing,
  quarterSpacing,
  standardSpacing,
} from 'styles/global_defaults/scaffolding';

type Props = {
  children: ReactNode,
  title?: string,
  titleId?: string,
  className?: string,
  dataQa?: string,
};

const styles = css`
  overflow: hidden;
  position: relative;
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: ${doubleSpacing}px ${extraLargeSpacing}px;
  background: ${white};
  border-radius: ${halfSpacing}px;
  box-shadow: 0 2px 4px 0 ${gray6};

  > :not(.layer-background) + :not(.layer-background) {
    margin-top: ${standardSpacing}px;
  }

  @media(min-width: ${screenMdMax}px) {
    width: fit-content;
    min-width: 920px;
  }

  ${handheld(css`
    padding: ${largeSpacing + quarterSpacing}px ${halfSpacing}px;
  `)};

  .layer-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 55%;
    background: ${info};

    ${handheld(css`
      max-height: 285px;
    `)};
  }

  .header {
    z-index: 1;
    color: ${black};
    text-align: center;
  }

  .title {
    margin: 0;
    font-size: ${textExtraLargeFontSize}px;
    font-weight: ${semiBoldFontWeight};
    line-height: ${headerRegularLineHeight}px;
  }

  .subtitle {
    margin: 0;
    font-size: ${textMediumFontSize + 2}px;
    font-weight: ${normalFontWeight};
    line-height: ${textLargeLineHeight}px;
  }

  .offering-cards {
    z-index: 1;

    ${handheld(css`
      flex-direction: column;
      align-items: center;

      > * + * {
        margin-top: ${standardSpacing}px;
      }
    `)};
  }

  .button {
    box-shadow: none;
  }
`;

const DiscoveryShelf = ({
  children,
  title,
  titleId,
  className,
  dataQa,
}: Props) => (
  <section
    className={`d-flex flex-column align-items-center${className ? ` ${className}` : ''}`}
    css={styles}
    aria-labelledby={title ? titleId : undefined}
    data-qa={dataQa}
  >
    <div className='layer-background' />
    {title && (
      <div className='header'>
        <h2 className='title' id={titleId}>{title}</h2>
      </div>
    )}
    {children}
  </section>
);

export default DiscoveryShelf;
